/**
 * Escrow exporter. STRICTLY READ-ONLY:
 * - Opens 'adia-db' via indexedDB.open(name) WITHOUT a version number, so an
 *   EXISTING database opens at its current version and no upgrade can run.
 *   A MISSING database is the one case where a versionless open is not
 *   passive: the spec creates it at version 1 and fires upgradeneeded. The
 *   handler below aborts that upgrade, which leaves no database behind, and
 *   reports 'idb-absent'. See the code comment there for why silently
 *   creating it would poison the app's upgrade ladder permanently.
 * - Accepts every shipped database version and captures the inventory that is
 *   actually present. This lets historical and partially damaged profiles be
 *   salvaged without guessing at ownership or treating the archive as an
 *   automatically restorable schema.
 * - Enumerates objectStoreNames dynamically (forward-compatible with stores
 *   this code does not know about) and uses 'readonly' transactions only.
 * - Never writes, mutates, marks synced, deletes, or pushes anything.
 *
 * exportEscrow captures TWICE (two independent full passes) and refuses to
 * emit an archive unless both passes produce the same contentDigest
 * (digest over everything except contextId/captureStart/captureEnd).
 *
 * localStorage is mandatory. A completed capture carries an explicit
 * captureStatus, so entries: [] means accessible-and-empty. Accessor,
 * enumeration, getItem, or partial-read failure aborts the whole capture.
 *
 * MEMORY: the whole archive is assembled in memory, and audio Blobs are
 * base64 (about 4/3 of raw). The audio queue's soft cap is 5 rows / 25 MB
 * (core rule #25), so the transient peak stays bounded even on a phone, and
 * the bulky scripture cache is summary-only. If a future store can grow
 * without a cap, stream it instead of widening this path.
 */

import {
  ARCHIVE_VERSION,
  COMPATIBLE_IDB_VERSIONS,
  ESCROW_CAPTURE_ORIGIN,
  SEMANTIC_EMPTY_EVIDENCE_VERSION,
  EscrowError,
  isSafeContextId,
  isSafeStoreName,
  SUMMARY_ONLY_STORES,
  type EncodedValue,
  type EscrowEnvelope,
  type EscrowLocalStorageCapture,
  type EscrowLocalStorageEntry,
  type EscrowManifest,
  type EscrowMetadata,
  type EscrowReceipt,
  type EscrowRow,
  type EscrowStore,
  type SemanticEmptyEvidence,
} from './types';
import {
  canonicalStringify,
  computeKeySetDigest,
  computeManifestContentDigest,
  computeManifestDigest,
  computeRowDigest,
  computeStoreContentDigest,
  encodeCanonical,
  sha256Hex,
  unsupportedEncounterCount,
} from './canonical';
import { assertStrongPassphrase, encryptJson } from './crypto';
import { hasSemanticRecoveryContent } from './semantic';

const DB_NAME = 'adia-db';

function assertSafeContextId(value: unknown): asserts value is string {
  if (!isSafeContextId(value)) throw new EscrowError('invalid-context-id');
}

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(new EscrowError('idb-read-failed'));
  });
}

function openExisting(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const fail = (error: EscrowError) => {
      if (settled) return;
      settled = true;
      reject(error);
    };
    // No version argument: an EXISTING database opens at its current version
    // and upgradeneeded never fires.
    const r = indexedDB.open(DB_NAME);
    // A MISSING database is the exception: a versionless open CREATES it at
    // version 1 and fires upgradeneeded. Letting that happen would be a write
    // from a read-only exporter, and worse, it would permanently poison the
    // app: the next real open at the current DB_VERSION would see oldVersion
    // 1, skip the `oldVersion < 1` clause that creates the 'prayers' store,
    // and then abort the whole versionchange when a later clause reaches for
    // that store. The profile could never open 'adia-db' again. Aborting the
    // upgrade here leaves nothing behind (the create is rolled back) and
    // reports the real condition: this origin/profile/container has no
    // database, so the capture is pointed at the wrong place.
    r.onupgradeneeded = () => {
      try {
        r.transaction?.abort();
      } catch {
        // Abort is best-effort; the rejection below is what the caller sees.
      }
      fail(new EscrowError('idb-absent'));
    };
    r.onsuccess = () => {
      // A blocked request can become successful after we have already failed
      // it. Close that late connection so the failed capture leaves no handle
      // behind that could block later recovery work.
      if (settled) {
        r.result.close();
        return;
      }
      settled = true;
      resolve(r.result);
    };
    r.onerror = () => fail(new EscrowError('idb-open-failed'));
    r.onblocked = () => fail(new EscrowError('idb-open-failed'));
  });
}

function assertCompatibleDatabase(db: IDBDatabase): void {
  if (!COMPATIBLE_IDB_VERSIONS.includes(db.version)) {
    throw new EscrowError('idb-incompatible-version');
  }
  const actual = Array.from(db.objectStoreNames);
  if (actual.some((name) => !isSafeStoreName(name))) {
    throw new EscrowError('idb-incompatible-schema');
  }
}

interface LocalStorageSnapshotEntry {
  key: string;
  value: string;
}

interface LocalStorageSnapshot {
  capture: EscrowLocalStorageCapture;
  allEntries: LocalStorageSnapshotEntry[];
}

/**
 * Capture one coherent localStorage inventory. Every enumerated key is read,
 * including non-adia keys, so a storage facade that permits the first item
 * and denies a later getItem cannot masquerade as a complete empty capture.
 */
function captureLocalStorage(): LocalStorageSnapshot {
  let ls: Storage | undefined;
  try {
    ls = (globalThis as { localStorage?: Storage }).localStorage;
  } catch {
    throw new EscrowError('localstorage-unavailable');
  }
  if (!ls) throw new EscrowError('localstorage-unavailable');

  const allEntries: LocalStorageSnapshotEntry[] = [];
  const seen = new Set<string>();
  let initialLength: number;
  try {
    initialLength = ls.length;
    if (!Number.isSafeInteger(initialLength) || initialLength < 0) {
      throw new Error('invalid-localstorage-length');
    }
    for (let i = 0; i < initialLength; i++) {
      const key = ls.key(i);
      if (typeof key !== 'string' || seen.has(key)) {
        throw new Error('partial-localstorage-enumeration');
      }
      const value = ls.getItem(key);
      if (typeof value !== 'string') {
        throw new Error('partial-localstorage-read');
      }
      seen.add(key);
      allEntries.push({ key, value });
    }
    if (ls.length !== initialLength) throw new Error('localstorage-mutated-during-read');
  } catch {
    throw new EscrowError('localstorage-read-failed');
  }

  const entries: EscrowLocalStorageEntry[] = allEntries
    .filter((entry) => entry.key.startsWith('adia-'))
    .map(({ key, value }) => ({ key, value }));
  entries.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  return { capture: { captureStatus: 'complete', entries }, allEntries };
}

/** Best-effort observed auth identity from Supabase's localStorage session. */
function observeAuth(
  entries: LocalStorageSnapshotEntry[],
): { userId: string | null; email: string | null } {
  for (const { key, value } of entries) {
    if (!/auth-token/.test(key)) continue;
    try {
      const parsed = JSON.parse(value) as { user?: { id?: string; email?: string } };
      const user = parsed?.user;
      if (user && (user.id || user.email)) {
        return { userId: user.id ?? null, email: user.email ?? null };
      }
    } catch {
      // Identity is evidence only. Malformed JSON is not an access failure;
      // the exact bytes were read successfully and non-auth adia-* bytes are
      // still captured below.
    }
  }
  return { userId: null, email: null };
}

async function captureStore(db: IDBDatabase, name: string): Promise<EscrowStore> {
  const summaryOnly = SUMMARY_ONLY_STORES.includes(name);
  // Sampled as a DELTA around this store's encode work (see canonical.ts).
  // Any increment means a value was replaced by a placeholder tag and its
  // bytes are gone, while every digest below would still recompute cleanly
  // over the placeholder. Fail the whole capture rather than emit an archive
  // that verifies green with content missing.
  const unsupportedBefore = unsupportedEncounterCount();

  // LOAD-BEARING: every IDB request for this store is issued SYNCHRONOUSLY,
  // back to back, before the first await. An IndexedDB transaction goes
  // inactive once control returns to the event loop with no pending requests,
  // and awaiting a WebCrypto digest (as the encode/digest work below does)
  // yields a full task. Issuing getAll() after that await throws
  // TransactionInactiveError on real browsers. Read everything first, then
  // encode and digest outside the transaction's lifetime.
  const tx = db.transaction(name, 'readonly');
  const store = tx.objectStore(name);
  const keysReq = req(store.getAllKeys());
  const valuesReq = summaryOnly ? null : req(store.getAll());
  // Mark the second promise handled so a getAllKeys() failure cannot surface
  // as an unhandled rejection. Awaiting the original below still throws.
  if (valuesReq) valuesReq.catch(() => {});

  const keys = await keysReq;
  const values = valuesReq ? await valuesReq : [];

  const encodedKeys: EncodedValue[] = [];
  for (const k of keys) encodedKeys.push(await encodeCanonical(k));
  const keySetDigest = await computeKeySetDigest(encodedKeys);

  if (summaryOnly) {
    // Re-downloadable scripture cache: key counts + key digest only.
    if (unsupportedEncounterCount() !== unsupportedBefore) {
      throw new EscrowError('unrepresentable-value');
    }
    const summary = {
      name,
      rowCount: keys.length,
      keySetDigest,
      summaryOnly: true as const,
    };
    return { ...summary, contentDigest: await computeStoreContentDigest(summary) };
  }

  const rows: EscrowRow[] = [];
  const rowDigests: string[] = [];
  for (let i = 0; i < keys.length; i++) {
    const key = encodedKeys[i];
    const value = await encodeCanonical(values[i]);
    const digest = await computeRowDigest(key, value);
    rows.push({ key, value, digest });
    rowDigests.push(digest);
  }
  if (unsupportedEncounterCount() !== unsupportedBefore) {
    throw new EscrowError('unrepresentable-value');
  }
  const contentDigest = await computeStoreContentDigest({
    name,
    rowCount: rows.length,
    keySetDigest,
    rowDigests,
  });
  return { name, rowCount: rows.length, keySetDigest, rows, contentDigest };
}

export interface CaptureOptions {
  /** Exact identifier from the already-created private context ledger. */
  readonly contextId: string;
  readonly contextLabel: string;
}

/** One full read-only pass over adia-db + adia-* localStorage. */
export async function captureManifest(opts: CaptureOptions): Promise<EscrowManifest> {
  // This is a public probe/capture primitive as well as an export building
  // block. Refuse a missing or unsafe ledger identity before opening IDB or
  // touching localStorage, even when a caller bypasses exportEscrow().
  const contextId = opts?.contextId;
  assertSafeContextId(contextId);
  const captureOrigin = (globalThis as { location?: { origin?: string } })
    .location?.origin ?? null;
  if (captureOrigin !== ESCROW_CAPTURE_ORIGIN) {
    throw new EscrowError('capture-origin-mismatch');
  }
  const contextLabel = opts.contextLabel;
  const captureStart = new Date().toISOString();
  const db = await openExisting();
  try {
    assertCompatibleDatabase(db);
    const localStorageSnapshot = captureLocalStorage();
    const storeNames = Array.from(db.objectStoreNames).sort();
    const stores: EscrowStore[] = [];
    for (const name of storeNames) stores.push(await captureStore(db, name));

    const auth = observeAuth(localStorageSnapshot.allEntries);
    const win = globalThis as {
      navigator?: { userAgent?: string };
      __ADIA_RELEASE_CAPABILITIES__?: { buildId?: string };
    };
    const metadata: EscrowMetadata = {
      observedAuthUserId: auth.userId,
      observedAuthEmail: auth.email,
      origin: captureOrigin,
      buildId: win.__ADIA_RELEASE_CAPABILITIES__?.buildId ?? null,
      idbVersion: db.version ?? null,
      userAgent: win.navigator?.userAgent ?? null,
    };

    const base = {
      archiveVersion: ARCHIVE_VERSION,
      contextId,
      contextLabel,
      captureStart,
      captureEnd: new Date().toISOString(),
      metadata,
      stores,
      localStorage: localStorageSnapshot.capture,
    };
    const contentDigest = await computeManifestContentDigest(base);
    const manifestDigest = await computeManifestDigest({
      ...base,
      digests: { contentDigest },
    });
    return { ...base, digests: { contentDigest, manifestDigest } };
  } finally {
    db.close();
  }
}

export interface ExportEscrowOptions {
  /** Exact identifier from the already-created private context ledger. */
  readonly contextId: string;
  readonly contextLabel: string;
  readonly passphrase: string;
  /** Optional friendly filename stem for the in-app protected-backup flow. */
  readonly downloadBaseName?: string;
}

/**
 * Vacuous means every recoverable-content store is empty and neither
 * user-authored recovery key exists. Preferences such as adia-lang and
 * re-downloadable BSB cache rows do not make a capture non-empty: accepting
 * them as proof would let a wrong partition produce green no-loss evidence.
 */
async function assertNonVacuous(manifest: EscrowManifest): Promise<void> {
  if (!(await hasSemanticRecoveryContent(manifest))) {
    throw new EscrowError('empty-capture');
  }
}

/** Test-only hooks (probe): run between the two capture passes. */
export interface ExportEscrowTestHooks {
  betweenPasses?: () => void | Promise<void>;
}

export interface ExportEscrowResult {
  receipt: EscrowReceipt;
  envelope: EscrowEnvelope;
  /** UTF-8 bytes of the envelope JSON (the .escrow file contents). */
  bytes: Uint8Array;
}

export interface SemanticEmptyEvidenceOptions extends CaptureOptions {
  /** Exact canonical record IDs already registered outside this partition. */
  readonly settledVolatileRecordIds: readonly string[];
}

export interface SemanticEmptyEvidenceResult {
  evidence: SemanticEmptyEvidence;
  /** Exact downloaded JSON bytes. */
  bytes: Uint8Array;
  /** SHA-256 of bytes, for the private context ledger. */
  fileSha256: string;
}

function sanitizeLabel(label: string): string {
  return (
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'archive'
  );
}

function triggerDownload(bytes: Uint8Array, filename: string, mime: string): void {
  const doc = (globalThis as { document?: Document }).document;
  if (!doc) return;
  const src = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
  const blob = new Blob([src as ArrayBuffer], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = doc.createElement('a');
  a.href = url;
  a.download = filename;
  doc.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Prove the special volatile-only case without manufacturing an empty escrow
 * archive. The normal exporter remains non-vacuous and still throws
 * `empty-capture`. This separate operation double-reads the exact partition,
 * requires semantic emptiness, and emits only non-content evidence bound to a
 * nonempty, already-registered volatile record set.
 */
export async function verifySemanticEmptyContext(
  opts: SemanticEmptyEvidenceOptions,
  testHooks?: ExportEscrowTestHooks,
): Promise<SemanticEmptyEvidenceResult> {
  const contextId = opts?.contextId;
  assertSafeContextId(contextId);
  const contextLabel = opts.contextLabel;
  const suppliedIds = opts.settledVolatileRecordIds;
  if (!Array.isArray(suppliedIds) || suppliedIds.length === 0) {
    throw new EscrowError('invalid-volatile-record-set');
  }
  const suppliedCount = suppliedIds.length;
  const settledVolatileRecordIds: string[] = [];
  for (let index = 0; index < suppliedCount; index++) {
    const value = suppliedIds[index];
    if (typeof value !== 'string' || !/^[a-f0-9]{64}$/.test(value)) {
      throw new EscrowError('invalid-volatile-record-set');
    }
    settledVolatileRecordIds.push(value);
  }
  const canonicalIds = [...new Set(settledVolatileRecordIds)].sort();
  if (
    canonicalIds.length !== settledVolatileRecordIds.length ||
    canonicalIds.some((value, index) => value !== settledVolatileRecordIds[index])
  ) {
    throw new EscrowError('invalid-volatile-record-set');
  }

  const first = await captureManifest({ contextId, contextLabel });
  if (testHooks?.betweenPasses) await testHooks.betweenPasses();
  const second = await captureManifest({ contextId, contextLabel });
  if (first.digests.contentDigest !== second.digests.contentDigest) {
    throw new EscrowError('mutation-during-capture');
  }
  if (await hasSemanticRecoveryContent(first)) {
    throw new EscrowError('semantic-content-present');
  }

  const settledVolatileRecordSetDigest = await sha256Hex(
    canonicalStringify(settledVolatileRecordIds),
  );
  const evidenceBase = {
    evidenceVersion: SEMANTIC_EMPTY_EVIDENCE_VERSION,
    contextId: first.contextId,
    contextLabel: first.contextLabel,
    captureStart: first.captureStart,
    captureEnd: first.captureEnd,
    origin: ESCROW_CAPTURE_ORIGIN,
    buildId: first.metadata.buildId,
    contentDigest: first.digests.contentDigest,
    manifestDigest: first.digests.manifestDigest,
    exporterResult: 'empty-capture' as const,
    settledVolatileRecordIds,
    settledVolatileRecordCount: settledVolatileRecordIds.length,
    settledVolatileRecordSetDigest,
  };
  const evidence: SemanticEmptyEvidence = {
    ...evidenceBase,
    evidenceDigest: await sha256Hex(canonicalStringify(evidenceBase)),
  };
  const bytes = new TextEncoder().encode(JSON.stringify(evidence, null, 2));
  const fileSha256 = await sha256Hex(bytes);
  const stamp = first.captureStart.replace(/[:.]/g, '-');
  const baseName = `adia-semantic-empty-${sanitizeLabel(contextLabel)}-${stamp}`;
  triggerDownload(bytes, `${baseName}.json`, 'application/json');
  return { evidence, bytes, fileSha256 };
}

export async function exportEscrow(
  opts: ExportEscrowOptions,
  testHooks?: ExportEscrowTestHooks,
): Promise<ExportEscrowResult> {
  // Bind the primitive value once. In particular, a mutable console options
  // object or accessor cannot substitute a different context between
  // validation and either capture pass.
  const contextId = opts?.contextId;
  assertSafeContextId(contextId);
  const contextLabel = opts.contextLabel;
  const passphrase = opts.passphrase;
  // Fail before reading any device content when the archive could not be
  // protected by an acceptable creation-time passphrase.
  assertStrongPassphrase(passphrase);
  const first = await captureManifest({ contextId, contextLabel });
  if (testHooks?.betweenPasses) await testHooks.betweenPasses();
  const second = await captureManifest({ contextId, contextLabel });
  if (first.digests.contentDigest !== second.digests.contentDigest) {
    throw new EscrowError('mutation-during-capture');
  }
  // Checked after the two passes agree, so an empty-capture report is about
  // the device and not about a race.
  await assertNonVacuous(first);

  const envelope = await encryptJson(first, passphrase);
  const bytes = new TextEncoder().encode(JSON.stringify(envelope));
  const ciphertextSha256 = await sha256Hex(bytes);

  const counts = Object.create(null) as Record<string, number>;
  for (const s of first.stores) counts[s.name] = s.rowCount;

  const receipt: EscrowReceipt = {
    archiveVersion: ARCHIVE_VERSION,
    contextId: first.contextId,
    contextLabel: first.contextLabel,
    counts,
    ciphertextSha256,
    contentDigest: first.digests.contentDigest,
    buildId: first.metadata.buildId,
    manifestDigest: first.digests.manifestDigest,
    origin: first.metadata.origin,
    captureStart: first.captureStart,
    captureEnd: first.captureEnd,
  };

  const stamp = first.captureStart.replace(/[:.]/g, '-');
  const requestedBaseName = opts.downloadBaseName?.trim();
  const baseName = requestedBaseName
    ? `${sanitizeLabel(requestedBaseName)}-${stamp}`
    : `adia-escrow-${sanitizeLabel(contextLabel)}-${stamp}`;
  triggerDownload(bytes, `${baseName}.escrow`, 'application/octet-stream');
  triggerDownload(
    new TextEncoder().encode(JSON.stringify(receipt, null, 2)),
    `${baseName}.receipt.json`,
    'application/json',
  );

  return { receipt, envelope, bytes };
}

// Re-exported for the probe's "exporter wrote nothing" serialization check.
export { canonicalStringify, encodeCanonical };
